// Account-link payload decoding ("SH1").
//
// Port of sh-overwolf/src/core/utils/account-link.ts (the ENCODER, which is the
// authority). The Overwolf app opens the account pages (/ow-plan, and later
// /ow-cancel) with a `d` query param carrying the signed-in user's e-mail,
// password and current plan, so the page never has to ask them to log in again.
//
// WHAT THIS IS AND IS NOT: SH1 is obfuscation, not protection. The encoder ships
// in the Overwolf client bundle and the scheme is documented on both sides (it
// has to be - this file is the other side), so anyone holding a link can recover
// the password from it, and that password is not revocable the way a token would
// be. The trade-off was raised and accepted deliberately; it is recorded in
// sh-overwolf/docs/superpowers/specs/2026-08-21-no-plan-tier-and-account-links-design.md
// along with the token-based alternatives that were offered instead.
//
// The discipline that goes with that decision, and that callers here must keep:
// LOG THAT A LINK WAS OPENED, NEVER THE URL AND NEVER A DECODED FIELD. Songhunt's
// diagnostic logs are downloaded by users and pasted into support tickets.
//
// ============================ THE SH1 SCHEME ============================
//
// Encode:
//   1. Join with U+001F:  SH1 <US> email <US> password <US> plan
//      (U+001F cannot occur in an e-mail, a password or a plan name, so no field
//      ever needs escaping.)
//   2. UTF-8 encode.
//   3. XOR every byte with the repeating XOR_KEY.
//   4. Reverse the bytes.
//   5. Standard base64, '=' padding stripped.
//   6. Transliterate the standard base64 alphabet to SH_ALPHABET, position for
//      position - 64 URL-safe characters, so no percent-encoding ever happens.
//
// The password may be EMPTY: the app sends the link anyway when it has no stored
// password, and the page asks for one. Everything else is required.
//
// Decode runs those six steps backwards.
//
// ------------------------- WIRE COMPATIBILITY -------------------------
// Two things changed on the app side (2026-08-26), and this decoder reads the
// old and the new shape of each. An Overwolf client updates on its own schedule,
// so links minted by an older build are still arriving and must not dead-end:
//
//   FIELD COUNT. The payload was five fields; the trailing `issuedAtMs` was
//   dropped. Four is now the shape. Five still decodes, and the timestamp is
//   kept for `isFresh` - a new link has none and so can never be expired.
//
//   PLAN NAME. The paid $5.00 tier goes over the wire as `personal`; it used to
//   be `hunter`. Same plan, same price, same Stripe product - `hunter` is now a
//   legacy alias normalised to `personal` on the way in, so the rest of the app
//   only ever sees one word for it.
//
// FRONT TRUNCATION. Because of the reversal in step 4 the SH1 marker ends up at
// the TAIL of the blob, so a blob cut at the FRONT keeps a valid marker while
// losing the end of the LAST field. On the five-field payload that field was
// `issuedAtMs`, which nothing validated: e-mail, password and plan came through
// intact and the page signed the right person in - the sign-in is what validates
// the payload, which is why /ow-plan can go without the freshness check (see
// ACCOUNT_LINK_MAX_AGE_MS). On the four-field payload the last field is `plan`,
// which IS validated, so front truncation now fails the plan check instead of
// sliding through. Dropping the timestamp made this stricter, not looser.
// =======================================================================

/** Payload marker + version. Field 0 of every payload. */
export const ACCOUNT_LINK_VERSION = 'SH1';

/** Field delimiter: ASCII UNIT SEPARATOR (U+001F). */
export const FIELD_SEPARATOR = '\u001F';

/** Repeating XOR key (step 3). Must match the encoder byte for byte. */
export const XOR_KEY = 'h#7Kq2$Lz9';

/** The standard base64 alphabet (step 5's output). */
export const STD_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** The substitution alphabet (step 6). Same 64 slots, URL-safe characters. */
export const SH_ALPHABET = 'kgRVPacDA5SBuziUnf8M97Ex4p0r3TmWsohO-jdeF2QKJL1IZvHY_NbGlwtXy6qC';

/** How long a link stays usable. Confirmed with the product owner 2026-08-21. */
export const ACCOUNT_LINK_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

/**
 * The plan the user is on, as sent by the Overwolf app.
 *
 * `none` is NOT "new account": it is "has bought nothing", which covers both a
 * user inside their free month and one whose free month has ended. Either way
 * both paid plans are purchases from there, which is how /ow-plan already treats
 * it - `none` ranks below both, so both are upgrades and both go to checkout.
 */
export type AccountPlan = 'none' | 'personal' | 'streamer';

export interface AccountPayload {
  email: string;
  password: string;
  plan: AccountPlan;
  /**
   * Milliseconds since the epoch, at the moment the link was built - or null,
   * which is now the normal case: the app stopped sending it. Only a link from
   * an older client carries one.
   */
  issuedAtMs: number | null;
}

/**
 * The wire word for a plan, normalised.
 *
 * A switch rather than a lookup object on purpose: an object indexed by an
 * arbitrary string out of a URL answers for '__proto__' and 'constructor' too.
 */
function normalizePlan(wire: string): AccountPlan | null {
  switch (wire) {
    case 'none':
      return 'none';
    case 'personal':
      return 'personal';
    // Legacy: what the $5.00 tier was called on the wire before 2026-08-26.
    case 'hunter':
      return 'personal';
    case 'streamer':
      return 'streamer';
    default:
      return null;
  }
}

function xorBytes(bytes: Uint8Array): Uint8Array {
  const key = new TextEncoder().encode(XOR_KEY);
  const out = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    out[i] = bytes[i] ^ key[i % key.length];
  }
  return out;
}

function transliterate(text: string, from: string, to: string): string {
  let out = '';
  for (const char of text) {
    const index = from.indexOf(char);
    // A character outside the source alphabet means the blob is not ours; pass
    // it through so decoding fails on the version check rather than here.
    out += index === -1 ? char : to[index];
  }
  return out;
}

/**
 * Decode a `d` blob. Returns null for anything that is not a well-formed SH1
 * payload - missing, truncated, tampered with, or built by something else.
 *
 * Freshness is NOT checked here: `isFresh` is separate so a caller can decode
 * and report "expired" distinctly from "malformed" if it ever needs to.
 */
export function decodeAccountPayload(blob: string): AccountPayload | null {
  try {
    if (!blob) return null;

    const base64 = transliterate(blob, SH_ALPHABET, STD_ALPHABET);
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    const joined = new TextDecoder().decode(xorBytes(bytes.reverse()));
    const fields = joined.split(FIELD_SEPARATOR);
    // Four is the current shape; five is a link minted by an older client.
    if (fields.length !== 4 && fields.length !== 5) return null;

    const [version, email, password, wirePlan, issuedAtMs] = fields;
    if (version !== ACCOUNT_LINK_VERSION) return null;
    if (!email) return null;

    const plan = normalizePlan(wirePlan);
    if (!plan) return null;

    // An EMPTY password is a valid payload, not a broken one. The app builds the
    // link with `password ?? ''` when the session was restored without a stored
    // password (sh-overwolf account-links.ts), on the understanding that the page
    // asks for one instead of losing the entry point. Rejecting it here turned
    // that into a dead end. The page decides what to do with it; the decoder's
    // job is only to say what the blob contained.

    // A legacy payload that carries a timestamp must carry a usable one - the
    // field was validated before this change and there is no reason to loosen it
    // now. A four-field payload has none, which is not a defect.
    let issuedAt: number | null = null;
    if (fields.length === 5) {
      issuedAt = Number(issuedAtMs);
      if (!Number.isFinite(issuedAt) || issuedAt <= 0) return null;
    }

    return { email, password, plan, issuedAtMs: issuedAt };
  } catch {
    return null;
  }
}

/**
 * True while the link is still usable. A link from the future is rejected too:
 * a clock skew big enough to matter means the timestamp cannot be trusted as an
 * expiry at all, and accepting it would hand out an unbounded window.
 *
 * A payload with no timestamp can never be called fresh, because nothing in it
 * says when it was minted. That is every link the current app builds, so turning
 * this back on at a call site would reject them all - the honest outcome, and the
 * reason expiry would have to be re-introduced on the app side first. /ow-plan
 * does not call this: these links do not expire (confirmed 2026-08-26).
 */
export function isFresh(payload: AccountPayload, now: number = Date.now()): boolean {
  if (payload.issuedAtMs == null) return false;

  const age = now - payload.issuedAtMs;
  return age >= -ACCOUNT_LINK_MAX_AGE_MS && age <= ACCOUNT_LINK_MAX_AGE_MS;
}
